import { faSearch, faSpinner } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { t } from '@lingui/core/macro';
import { Input } from '@/components/generic-controls';
import React, { ChangeEventHandler, useCallback, useEffect, useState } from 'react';
import { useDebounce, usePrevious } from '@/utils/hooks';
import { classnames } from '@/utils/html';
import theme from './SearchInput.module.scss';

interface Props {
    isLoading?: boolean;
    searchText?: string;
    onSearchTextChange?: (v: string) => void;
    className?: string;
}

const SearchInput: React.FC<Props> = ({ isLoading, searchText = '', onSearchTextChange, className }) => {
    const [internalText, setInternalText] = useState(searchText);

    useEffect(() => {
        setInternalText(searchText);
    }, [searchText]);

    const [isTyping, setIsTyping] = useState(false);

    const searchTextDebounced = useDebounce(internalText, 250);
    const previousSearchTextDebounced = usePrevious(searchTextDebounced);
    useEffect(() => {
        if (
            onSearchTextChange &&
            previousSearchTextDebounced !== undefined &&
            searchTextDebounced !== previousSearchTextDebounced
        ) {
            onSearchTextChange(searchTextDebounced || '');
            setIsTyping(false);
        }
    }, [searchTextDebounced, previousSearchTextDebounced, onSearchTextChange]);

    const handleTextChange: ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
        setIsTyping(true);
        setInternalText(e.target.value);
    }, []);

    const showSpinner = isLoading || isTyping;

    return (
        <div className={classnames(theme.inputContainer, className)}>
            <div>
                <Input
                    type="text"
                    className="form-control"
                    value={internalText}
                    onChange={handleTextChange}
                    placeholder={t`Search...`}
                />

                <FontAwesomeIcon
                    style={{ opacity: !showSpinner ? 1 : 0, transitionDelay: !showSpinner ? '.25s' : '0s' }}
                    icon={faSearch}
                    className={theme.icon}
                />
                <FontAwesomeIcon style={{ opacity: showSpinner ? 1 : 0 }} icon={faSpinner} spin className={theme.icon} />
            </div>
        </div>
    );
};

export default SearchInput;
